// SAB-1046 follow-up: ONE player avatar, so a headshot looks the same wherever it appears.
//
// Two implementations existed before this: PlayerCardMobile's `PlayerAvatar` (monogram
// initials + a shield for DST) and NFLMyTeamPage's inline block (position abbreviation as
// the fallback). Adding a third for the standings overlay and the matchup rows is how the
// first two came to disagree, so this is extracted rather than copied.
//
// SIZE is a prop because the callers genuinely differ: 36px in a players list, ~28px in a
// matchup row where the width budget is the tightest in the app.
import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NFLPlayerAvatarProps {
  fullName: string;
  position?: string | null;
  headshotUrl?: string | null;
  /** Tailwind size classes. Callers own their dimensions; 'h-7 w-7' is the matchup size. */
  className?: string;
  /** Monogram size, since callers range from 24px to 40px. */
  textClassName?: string;
}

/** First letters of the first two name parts. "Brian Thomas Jr." -> "BT". */
export function initialsOf(fullName: string): string {
  return (fullName || '')
    .split(' ')
    .map(n => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function NFLPlayerAvatar({
  fullName, position, headshotUrl, className, textClassName,
}: NFLPlayerAvatarProps) {
  const box = cn('rounded-full shrink-0 overflow-hidden flex items-center justify-center', className ?? 'h-9 w-9');

  // A team defense is not a person and has no headshot anywhere -- 32 of the 64 rows the
  // SAB-1053 backfill can never fill are DSTs. A monogram of the team name would look like
  // a failed photo load; the shield says "this is a unit" on purpose.
  if (position === 'DST') {
    return (
      <div aria-label="Defense" className={cn(box, 'bg-slate-500/15 border border-slate-500/30')}>
        <Shield className="h-1/2 w-1/2 text-slate-300" />
      </div>
    );
  }

  if (headshotUrl) {
    return (
      <img
        src={headshotUrl}
        alt={fullName}
        loading="lazy"
        className={cn(box, 'object-cover bg-muted')}
        // Fall back to the monogram rather than a broken-image glyph. `display:none` was
        // the old behaviour and it collapsed the row's alignment: the flex gap stayed but
        // the box vanished, so every row with a dead URL sat 36px left of its neighbours.
        onError={(e) => {
          const el = e.currentTarget;
          el.style.display = 'none';
          el.insertAdjacentHTML('afterend',
            `<div class="${cn(box, 'bg-muted border border-border')}"><span class="${textClassName ?? 'text-[10px]'} font-semibold text-muted-foreground">${initialsOf(fullName)}</span></div>`);
        }}
      />
    );
  }

  return (
    <div className={cn(box, 'bg-muted border border-border')}>
      <span className={cn(textClassName ?? 'text-[10px]', 'font-semibold text-muted-foreground')}>
        {initialsOf(fullName)}
      </span>
    </div>
  );
}
